import React from 'react';
import { motion } from "framer-motion";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

const Museum = () => {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@300;400&display=swap');

        .mus-wrap {
          background: #fff;
          min-height: 100vh;
          font-family: Arial, Helvetica, sans-serif;
          color: #111;
          -webkit-font-smoothing: antialiased;
          padding-top: 150px;
        }

        .mus-hero {
          position: relative;
          width: 90%;
          height: min(48vh, 420px);
          overflow: hidden;
          background: #1a1612;
          margin: 0 auto;
          border-radius: 20px;
        }
        .mus-hero img {
          width: 100%; height: 100%;
          object-fit: cover; object-position: center 30%;
          filter: brightness(0.52) saturate(0.65);
          display: block;
          transform: scale(1.04);
          transition: transform 8s ease;
          border-radius: 20px;
        }
        .mus-hero:hover img { transform: scale(1); }
        .mus-hero::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(to bottom, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0) 55%, rgba(255,255,255,0.55) 100%);
        }
        .mus-hero-label {
          position: absolute; bottom: 0; left: 0; z-index: 2;
          padding: clamp(1.5rem, 5vw, 3rem) clamp(1.5rem, 7vw, 5rem);
        }
        .mus-hero-eyebrow {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 9px; font-weight: 400;
          letter-spacing: 0.3em; text-transform: uppercase;
          color: rgba(255,255,255,0.5);
          margin-bottom: 0.6rem;
          display: flex; align-items: center; gap: 10px;
        }
        .mus-hero-eyebrow::before { content:''; width:24px; height:1px; background:rgba(255,255,255,0.35); }
        .mus-hero-title {
          font-family: Arial, Helvetica, sans-serif;
          font-size: clamp(2.8rem, 8vw, 6.5rem);
          font-weight: 700; line-height: 0.92; letter-spacing: -0.03em;
          color: #fff;
        }
        .mus-hero-title em { font-style: italic; font-weight: 400; color: rgba(255,255,255,0.62); }

        /* ── HERO mobile ── */
        @media (max-width: 600px) {
          .mus-wrap {
            padding-top: 0;
          }
          .mus-hero {
            width: 94%;
            height: 200px;
            min-height: unset;
            border-radius: 16px;
            margin: 120px auto 0;
          }
          .mus-hero img {
            border-radius: 16px;
            filter: brightness(0.50) saturate(0.85);
          }
          .mus-hero-label {
            padding: 0.85rem 1rem 1rem;
          }
          .mus-hero-eyebrow {
            font-size: 7.5px;
            letter-spacing: 0.16em;
            margin-bottom: 0.35rem;
            gap: 6px;
          }
          .mus-hero-eyebrow::before {
            width: 14px;
            height: 1px;
          }
          .mus-hero-title {
            font-size: clamp(1.5rem, 5vw, 2.5rem);
            line-height: 1.1;
          }
        }
        .mus-hero-badge {
          position: absolute; top: clamp(1rem,3vw,2rem); right: clamp(1.5rem,5vw,3rem);
          z-index: 2;
          border: 1px solid rgba(255,255,255,0.18);
          backdrop-filter: blur(8px);
          background: rgba(255,255,255,0.07);
          padding: 0.4rem 0.9rem; border-radius: 2px;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 8px; letter-spacing: 0.24em; text-transform: uppercase;
          color: rgba(255,255,255,0.55);
        }

        .mus-body {
          max-width: 1100px; margin: 0 auto;
          padding: clamp(1.2rem, 3vw, 2.5rem) clamp(1.5rem, 7vw, 5rem) clamp(4rem, 8vw, 7rem);
        }

        .mus-meta {
          display: flex; align-items: center; gap: 2rem; flex-wrap: wrap;
          padding-bottom: 1.5rem; border-bottom: 1px solid #eee; margin-bottom: 2.5rem;
        }
        .mus-meta-item { display: flex; flex-direction: column; gap: 3px; }
        .mus-meta-key {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 8px; letter-spacing: 0.22em; text-transform: uppercase; color: #bbb;
        }
        .mus-meta-val { font-size: 0.85rem; font-weight: 500; color: #333; }
        .mus-meta-dot { width: 3px; height: 3px; border-radius: 50%; background: #e0e0e0; margin-top: 12px; }
        @media (max-width: 560px) { .mus-meta-dot { display: none; } }

        .mus-content {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: clamp(3rem, 6vw, 6rem); align-items: start;
        }
        @media (max-width: 720px) { .mus-content { grid-template-columns: 1fr; gap: 2.5rem; } }

        .mus-lead-label {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 8px; letter-spacing: 0.28em; text-transform: uppercase;
          color: #1d4ed8; display: flex; align-items: center; gap: 8px; margin-bottom: 1.2rem;
        }
        .mus-lead-label::before { content:''; width:14px; height:1px; background:#1d4ed8; }
        .mus-lead-heading {
          font-family: Arial, Helvetica, sans-serif;
          font-size: clamp(1.8rem, 4vw, 3rem); font-weight: 400;
          line-height: 1.1; letter-spacing: -0.025em; color: #111; margin-bottom: 1.8rem;
        }
        .mus-lead-heading em { font-style: italic; }
        .mus-lead-p {
          font-size: 1.05rem; line-height: 1.88; color: #444; font-weight: 300;
        }
        .mus-lead-p::first-letter {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 4.2em; font-weight: 700;
          float: left; line-height: 0.75; margin: 0.06em 0.1em 0 0; color: #111;
        }

        .mus-right { padding-top: 0.2rem; }
        .mus-body-p { font-size: 0.98rem; line-height: 1.88; color: #555; font-weight: 300; margin-bottom: 2.5rem; }

        .mus-quote {
          border-left: 3px solid #1d4ed8; padding: 1.2rem 1.5rem;
          background: #f0f5ff; margin-bottom: 2.5rem;
        }
        .mus-quote-text {
          font-family: Arial, Helvetica, sans-serif;
          font-size: clamp(1rem, 2vw, 1.3rem); font-style: italic;
          font-weight: 400; line-height: 1.55; color: #222;
        }

        .mus-curator {
          display: flex; align-items: center; gap: 1rem;
          padding: 1rem 1.2rem; border: 1px solid #eee;
          border-radius: 6px; background: #fff;
        }
        .mus-curator-av {
          width: 40px; height: 40px; border-radius: 50%;
          background: #f5f0eb; border: 1.5px solid #e0d5c8;
          display: flex; align-items: center; justify-content: center;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 1rem; color: #7a5c3e; flex-shrink: 0;
        }
        .mus-curator-lbl {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 7.5px; letter-spacing: 0.18em; text-transform: uppercase; color: #bbb; margin-bottom: 2px;
        }
        .mus-curator-name { font-size: 0.85rem; font-weight: 500; color: #333; }

        .mus-foot {
          border-top: 1px solid #eee;
          padding: 1.5rem clamp(1.5rem, 7vw, 5rem) 2.5rem;
          max-width: 1100px; margin: 0 auto;
          display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.6rem;
        }
        .mus-foot-l { font-family: Arial, Helvetica, sans-serif; font-size: 8px; letter-spacing: 0.18em; text-transform: uppercase; color: #ccc; }
        .mus-foot-r { font-family: Arial, Helvetica, sans-serif; font-size: 0.8rem; font-style: italic; color: #ccc; }
      `}</style>

      <div className="mus-wrap">

        <motion.div className="mus-hero" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9 }}>
          <img
            src="https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2Fmuseum.jpg?alt=media&token=60cf2fe5-f192-4287-943c-9eb397e16eb8"
            alt="The BCS Museum, Calabar"
          />
          <div className="mus-hero-badge">Est. Early 1970s</div>
          <motion.div className="mus-hero-label" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <p className="mus-hero-eyebrow">Brotherhood of the Cross and Star</p>
            <h1 className="mus-hero-title">The BCS<br /><em></em></h1>
          </motion.div>
        </motion.div>

        <div className="mus-body">
          <motion.div className="mus-meta" {...fadeUp(0.3)}>
            <div className="mus-meta-item">
              <span className="mus-meta-key">Location</span>
              <span className="mus-meta-val">Calabar, Nigeria</span>
            </div>
            <div className="mus-meta-dot" />
            <div className="mus-meta-item">
              <span className="mus-meta-key">Founded</span>
              <span className="mus-meta-val">Early 1970s</span>
            </div>
            <div className="mus-meta-dot" />
            <div className="mus-meta-item">
              <span className="mus-meta-key">First Curator</span>
              <span className="mus-meta-val">SCS Nsan Takon</span>
            </div>
            <div className="mus-meta-dot" />
            <div className="mus-meta-item">
              <span className="mus-meta-key">Collection</span>
              <span className="mus-meta-val">Sacred Artefacts</span>
            </div>
          </motion.div>

          <div className="mus-content">
            <motion.div {...fadeUp(0.45)}>
              <p className="mus-lead-label">Origins</p>
              <h2 className="mus-lead-heading">How the <em>Museum</em><br />came to be</h2>
              <p className="mus-lead-p">
                The BCS Museum came into being in the early 1970s when the Vestry Staff
                saw that the artefacts — gifts presented by BCS members and world
                organisations recognising the Supreme Deity of the Holy Spirit — had
                filled many rooms. Each item was a symbol of His authority and power,
                carefully preserved as testimony to the living presence of the Holy
                Spirit in the world.
              </p>
            </motion.div>

            <motion.div className="mus-right" {...fadeUp(0.6)}>
              <p className="mus-body-p">
                The first Curator of the modern BCS Museum was the late SCS Nsan Takon.
                He arranged and labelled the items in their classes — transforming a
                gathering of sacred objects into a structured archive, each piece
                documented and preserved for future generations. Today the Museum holds
                many historical items that form a pool of sacred objects where brethren
                and visiting guests find succour and solution to their spiritual problems.
              </p>
              <div className="mus-quote">
                <p className="mus-quote-text">
                  "The BCS Museum has more spiritual potency than ordinary museums in the world —
                  it is a sanctuary where the living power of the Holy Spirit resides."
                </p>
              </div>
              <div className="mus-curator">
                <div className="mus-curator-av">N</div>
                <div>
                  <p className="mus-curator-lbl">First Curator</p>
                  <p className="mus-curator-name">The late SCS Nsan Takon</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

         <div className="mus-foot">
          <span className="mus-foot-l">The BCS Museum · Brotherhood of the Cross and Star</span>
          <span className="mus-foot-r">Calabar, Nigeria · Est. Early 1970s</span>
        </div> 
      </div>
    </>
  );
};

export default Museum;
